import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, Video, Settings, CreditCard, ChevronRight, ChevronLeft, ChevronDown, Sun, Moon, Users, Plus, Minimize2, Maximize2, FileText, AlertCircle, Scissors, Film, Box, User, Share2, PlayCircle, MessageCircle, ExternalLink, Mic, Home } from 'react-feather';
import { useWorkspaceTab } from './WorkspaceTabContext';
import './SidebarLayout.css';
import { useDarkMode } from './DarkModeContext';
import CreditDisplay from './CreditDisplay';
import api from './api';
import { useCredits } from './CreditContext';
import { useWorkspace } from './WorkspaceContext';
import { useNews } from './NewsContext';
import WorkspaceSelector from './components/WorkspaceSelector';
import { isTokenExpired } from './utils/jwt';

const SidebarLayout = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [showTutorialModal, setShowTutorialModal] = useState(false);
    const [isFloating, setIsFloating] = useState(false);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const location = useLocation();
    const tabContext = useWorkspaceTab();
    const isWorkspaceRoute = location.pathname === '/workspace';
    const canSwitchTabs = isWorkspaceRoute && tabContext && typeof tabContext.setActiveTab === 'function';
    const videoRef = useRef(null);
    const floatingVideoRef = useRef(null);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const { darkMode, toggleDarkMode } = useDarkMode();
    const [credits, setCredits] = useState({ total: 1600, used: 0, remaining: 1600 });
    const { addCredits } = useCredits();
    const { currentWorkspace, createWorkspace, hasPermission, workspaceCounts } = useWorkspace();

    const getCurrentUserId = () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return null;
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.userId || payload.id || payload.sub || null;
        } catch {
            return null;
        }
    };
    const { showNotification, latestArticle, dismissArticle, openArticle } = useNews();
    
    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const toggleSidebar = () => {
        setCollapsed(!collapsed);
    };

    // Check if user is authenticated
    const isAuthenticated = () => {
        const token = localStorage.getItem('token');
        return token && !isTokenExpired(token);
    };

    // Add this function to fetch credits
    // const fetchCredits = async () => {
    //     // Don't fetch credits if user is not authenticated
    //     if (!isAuthenticated()) {
    //         console.log('User not authenticated, skipping credits fetch');
    //         return;
    //     }

    //     try {
    //         const response = await api.getUserCredits();
    //         if (response.data) {
    //             setCredits({
    //                 total: response.data.total || 1600,
    //                 used: response.data.used || 0,
    //                 remaining: response.data.remaining || 1600
    //             });
    //         }
    //     } catch (error) {
    //         console.error('Error fetching credits:', error);
    //     }
    // };

    // Fetch credits when component mounts (only if authenticated)
    // useEffect(() => {
    //     if (isAuthenticated()) {
    //         fetchCredits();
    //     }
    // }, []);

    // Workspace management functions
    const handleCreateWorkspace = async () => {
        const name = prompt('Enter workspace name:');
        if (name && name.trim()) {
            try {
                await createWorkspace({ name: name.trim() });
            } catch (error) {
                console.error('Failed to create workspace:', error);
                setErrorMessage('Failed to create workspace. Please try again.');
                setShowErrorModal(true);
            }
        }
    };

    // Auto-collapse sidebar on small screens
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 600) {
                setCollapsed(true);
            }
        };

        // Run on mount
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Drag functionality for floating modal
    const handleMouseDown = (e) => {
        if (!isFloating) return;
        setIsDragging(true);
        const rect = e.currentTarget.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging || !isFloating) return;
            setModalPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset, isFloating]);

    // Handle playback speed change
    const handleSpeedChange = (speed) => {
        setPlaybackSpeed(speed);
        
        // Apply speed to both videos
        if (videoRef.current) {
            videoRef.current.playbackRate = speed;
        }
        if (floatingVideoRef.current) {
            floatingVideoRef.current.playbackRate = speed;
        }
    };

    // Handle floating mode toggle
    const toggleFloating = () => {
        if (!isFloating) {
            // Switching to floating mode - save current video state
            if (videoRef.current) {
                const currentTime = videoRef.current.currentTime;
                const isPaused = videoRef.current.paused;
                const currentSpeed = videoRef.current.playbackRate;
                
                // Set initial position when entering floating mode
                setModalPosition({ x: 50, y: 50 });
                
                // Apply saved state to floating video after a short delay
                setTimeout(() => {
                    if (floatingVideoRef.current) {
                        floatingVideoRef.current.currentTime = currentTime;
                        floatingVideoRef.current.playbackRate = currentSpeed;
                        if (!isPaused) {
                            floatingVideoRef.current.play();
                        }
                    }
                }, 100);
            }
        } else {
            // Switching to fullscreen mode - save floating video state
            if (floatingVideoRef.current) {
                const currentTime = floatingVideoRef.current.currentTime;
                const isPaused = floatingVideoRef.current.paused;
                const currentSpeed = floatingVideoRef.current.playbackRate;
                
                // Apply saved state to fullscreen video after a short delay
                setTimeout(() => {
                    if (videoRef.current) {
                        videoRef.current.currentTime = currentTime;
                        videoRef.current.playbackRate = currentSpeed;
                        if (!isPaused) {
                            videoRef.current.play();
                        }
                    }
                }, 100);
            }
        }
        setIsFloating(!isFloating);
    };

    // Close modal and reset floating state
    const closeModal = () => {
        setShowTutorialModal(false);
        setIsFloating(false);
        setModalPosition({ x: 0, y: 0 });
    };


    const activeTab = tabContext?.activeTab ?? '';
    const setActiveTab = tabContext?.setActiveTab;

    const collapsedTooltip = (text) => (
        collapsed ? { 'data-tooltip': text, 'aria-label': text } : {}
    );

    const navItem = (tab, IconComponent, label, count = null) => {
        const isActive = activeTab === tab;
        const hasCount = count !== null && count !== undefined;
        const tooltipText = hasCount ? `${label} (${count})` : label;
        return (
            <button
                type="button"
                key={tab}
                className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setActiveTab?.(tab)}
                {...collapsedTooltip(tooltipText)}
            >
                <span className="sidebar-nav-icon"><IconComponent size={20} /></span>
                {!collapsed && (
                    <>
                        <span className="sidebar-nav-label">{label}</span>
                        {hasCount && (
                            <span className="sidebar-nav-count">{count}</span>
                        )}
                    </>
                )}
            </button>
        );
    };

    return (
        <div className={`layout-container ${darkMode ? 'dark-mode' : ''}`}>
            <div className={`sidebar sidebar--workspace ${collapsed ? 'collapsed' : ''} ${canSwitchTabs ? 'sidebar--tabs' : ''}`}>
                <div className="sidebar-toggle" onClick={toggleSidebar} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                    {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                </div>

                {/* <div className="sidebar-header">
                    {!collapsed ? <h3>Merra.ai</h3> : <h3>M.</h3>}
                </div> */}

                <div className="workspace-section">
                    {!collapsed && (
                        <div className="workspace-header">
                            <span className="workspace-section-label">Workspace</span>
                            <button
                                className="create-workspace-btn"
                                onClick={handleCreateWorkspace}
                                title="Create New Workspace"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    )}
                    <div className="workspace-selector-container">
                        <WorkspaceSelector />
                    </div>
                </div>

                {canSwitchTabs && (
                    <nav className="sidebar-nav sidebar-nav--sections">
                        {['673247877e4ac0a824cbac0d' /* Boukar */].includes(getCurrentUserId()) && (
                            navItem('home', Home, 'Home')
                        )}
                        {navItem('profile', User, 'Profile')}
                        {!collapsed && <div className="sidebar-section-label">LIBRARY</div>}
                        {collapsed && <div className="sidebar-nav-spacer sidebar-nav-spacer--after-break" aria-hidden="true" />}
                        {navItem('raw', Video, 'Raw Videos', workspaceCounts?.rawTotal ?? 0)}
                        {navItem('cuts', Scissors, 'Cuts', workspaceCounts?.cutsTotal ?? 0)}
                        {!collapsed && <div className="sidebar-section-label" style={{ marginTop: '12px' }}>CONTENT STUDIO</div>}
                        {collapsed && <div className="sidebar-nav-spacer sidebar-nav-spacer--after-break" aria-hidden="true" />}
                        {navItem('studio', Film, 'Studio')}
                        {(() => {
                            const tab = 'batch';
                            const isActive = activeTab === tab;
                            return (
                                <button
                                    type="button"
                                    key={tab}
                                    className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                                    onClick={() => setActiveTab?.(tab)}
                                    {...collapsedTooltip('Batch Creation')}
                                >
                                    <span className="sidebar-nav-icon"><Box size={20} /></span>
                                    {!collapsed && (
                                        <>
                                            <span className="sidebar-nav-label">Batch Creation</span>
                                            <span className="sidebar-pro-badge">Pro</span>
                                        </>
                                    )}
                                </button>
                            );
                        })()}
                        {navItem('generated', PlayCircle, 'My Generated Videos', workspaceCounts?.generatedTotal ?? 0)}
                        {!collapsed && <div className="sidebar-section-label" style={{marginTop:'12px'}}>SETTINGS</div>}
                        {collapsed && <div className="sidebar-nav-spacer sidebar-nav-spacer--after-break" aria-hidden="true" />}
                        {navItem('voice', Mic, 'Voice')}
                        {navItem('sharing', Share2, 'Members')}
                        {hasPermission('admin') && (currentWorkspace?.isSubscribed || ['673247877e4ac0a824cbac0d'].includes(getCurrentUserId())) && (
                            <button
                                type="button"
                                className="sidebar-nav-item"
                                onClick={async () => {
                                    try {
                                        const response = await api.createPortalSession(currentWorkspace._id);
                                        window.open(response.data.url, '_blank');
                                    } catch (error) {
                                        console.error('Error creating portal session:', error);
                                        setErrorMessage('Failed to open subscription management');
                                        setShowErrorModal(true);
                                    }
                                }}
                                {...collapsedTooltip('Manage Subscription')}
                            >
                                <span className="sidebar-nav-icon"><CreditCard size={20} /></span>
                                {!collapsed && (
                                    <>
                                        <span className="sidebar-nav-label">Manage Subscription</span>
                                        <ExternalLink size={14} className="sidebar-external-icon" aria-hidden />
                                    </>
                                )}
                            </button>
                        )}
                    </nav>
                )}

                <div className="sidebar-footer">
                    <button type="button" className="dark-mode-toggle" onClick={() => window.open('/news', '_blank')} {...collapsedTooltip('News')}>
                        <div className="nav-icon"><FileText size={20} /></div>
                        {!collapsed && <span>News</span>}
                        {!collapsed && <ExternalLink size={14} className="sidebar-external-icon" />}
                    </button>
                    <button type="button" className="dark-mode-toggle tutorial-toggle" onClick={() => setShowTutorialModal(true)} {...collapsedTooltip('Tutorial')}>
                        <div className="nav-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                                <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                            </svg>
                        </div>
                        {!collapsed && <span>Tutorial</span>}
                    </button>
                    <button type="button" className="dark-mode-toggle" onClick={toggleDarkMode} {...collapsedTooltip(darkMode ? 'Light Mode' : 'Dark Mode')}>
                        <div className="nav-icon">{darkMode ? <Sun size={20} /> : <Moon size={20} />}</div>
                        {!collapsed && <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>}
                    </button>
                </div>

                {/* <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    {!collapsed && (
                        <div style={{
                            fontSize: '10px',
                            color: 'var(--text-secondary)',
                            opacity: 0.6,
                            textAlign: 'center'
                        }}>
                            Last updated: {(() => {
                                try {
                                    const deployDate = process.env.REACT_APP_LAST_DEPLOY_DATE;
                                    if (deployDate) {
                                        const date = new Date(deployDate);
                                        if (!isNaN(date.getTime())) {
                                            return date.toLocaleString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            });
                                        }
                                    }
                                } catch (e) {
                                    console.warn('Failed to parse deploy date:', e);
                                }
                                return new Date().toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                });
                            })()}
                        </div>
                    )}
                </div> */}
            </div>
            
            <div className={`main-content ${collapsed ? 'expanded' : ''}`}>
                {children}
            </div>
            
            {/* Tutorial Modal */}
            {showTutorialModal && (
                <>
                    {!isFloating && (
                        <div className="tutorial-modal-overlay" style={{ zIndex: 10004 }} onClick={closeModal}>
                            <div className="tutorial-modal" style={{ zIndex: 10004 }} onClick={(e) => e.stopPropagation()}>
                                <div className="tutorial-modal-header">
                                    <h3>Video Editing Workflow Tutorial</h3>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <div className="speed-control">
                                            <label htmlFor="speed-select" style={{ fontSize: '12px', marginRight: '4px' }}>Speed:</label>
                                            <select 
                                                id="speed-select"
                                                value={playbackSpeed} 
                                                onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                                                className="speed-select"
                                            >
                                                <option value={0.5}>0.5x</option>
                                                <option value={0.75}>0.75x</option>
                                                <option value={1}>1x</option>
                                                <option value={1.25}>1.25x</option>
                                                <option value={1.5}>1.5x</option>
                                                <option value={2}>2x</option>
                                            </select>
                                        </div>
                                        <button 
                                            className="tutorial-modal-float"
                                            onClick={toggleFloating}
                                            title="Minimize to Floating Window"
                                        >
                                            <Minimize2 size={16} />
                                        </button>
                                        <button 
                                            className="tutorial-modal-close"
                                            onClick={closeModal}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                                <div className="tutorial-modal-content">
                                    <video 
                                        ref={videoRef}
                                        controls 
                                        width="100%" 
                                        height="auto"
                                        preload="metadata"
                                        crossOrigin="anonymous"
                                    >
                                        <source 
                                            src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Demo+Fast+Video+Creation.mp4"
                                            type="video/mp4"
                                        />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {isFloating && (
                        <div 
                            className="tutorial-modal-floating"
                            style={{
                                position: 'fixed',
                                left: `${modalPosition.x}px`,
                                top: `${modalPosition.y}px`,
                                zIndex: 10004,
                                cursor: isDragging ? 'grabbing' : 'grab'
                            }}
                            onMouseDown={handleMouseDown}
                        >
                            <div className="tutorial-modal-floating-header">
                                <h3>Video Tutorial</h3>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <div className="speed-control-floating">
                                        <select 
                                            value={playbackSpeed} 
                                            onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                                            className="speed-select-floating"
                                        >
                                            <option value={0.5}>0.5x</option>
                                            <option value={0.75}>0.75x</option>
                                            <option value={1}>1x</option>
                                            <option value={1.25}>1.25x</option>
                                            <option value={1.5}>1.5x</option>
                                            <option value={2}>2x</option>
                                        </select>
                                    </div>
                                    <button 
                                        className="tutorial-modal-unfloat"
                                        onClick={toggleFloating}
                                        title="Maximize to Fullscreen"
                                    >
                                        <Maximize2 size={16} />
                                    </button>
                                    <button 
                                        className="tutorial-modal-close"
                                        onClick={closeModal}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            <div className="tutorial-modal-floating-content">
                                <video 
                                    ref={floatingVideoRef}
                                    controls 
                                    width="300" 
                                    height="auto"
                                    preload="metadata"
                                >
                                    <source 
                                        src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Demo+Fast+Video+Creation.mp4" 
                                        type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* News Notification Modal */}
            {showNotification && latestArticle && window.innerWidth > 600 && (
                <div className="news-notification-modal">
                    <div className="news-notification-content">
                        <div className="news-notification-header">
                            <h4>New Article</h4>
                            <button 
                                className="news-notification-close"
                                onClick={() => dismissArticle(latestArticle._id)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="news-notification-body">
                            <h5 className="news-notification-title">{latestArticle.title}</h5>
                            <p className="news-notification-summary">{latestArticle.summary}</p>
                            <button 
                                className="news-notification-read-btn"
                                onClick={openArticle}
                            >
                                Read More
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Error Modal */}
            {showErrorModal && (
                <div className="modal" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowErrorModal(false);
                    }
                }}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '48px', 
                            marginBottom: '16px',
                            color: '#dc3545'
                        }}>
                            <AlertCircle size={48} />
                        </div>
                        <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                            Error
                        </h3>
                        <p style={{ 
                            marginBottom: '24px', 
                            color: '#666', 
                            fontSize: '16px',
                            lineHeight: '1.5'
                        }}>
                            {errorMessage}
                        </p>
                        <div className="modal-actions">
                            <button 
                                onClick={() => setShowErrorModal(false)}
                                style={{
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        );
    };

export default SidebarLayout;